import React, { useState } from "react";
import axios from "axios";
import { Mail, Clock, Globe, Send, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { GlowOrb, Overline } from "../components/SectionDecor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", message: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ state: "success", message: t.contact.form.successDesc });
      setForm({ name: "", email: "", company: "", phone: "", subject: "", message: "" });
    } catch (err) {
      setStatus({
        state: "error",
        message: err?.response?.data?.detail || t.contact.form.errorDesc,
      });
    }
  };

  return (
    <div data-testid="page-contact" className="relative">
      <GlowOrb color="#00d4ff" size={460} opacity={0.12} className="top-0 -left-20" />
      <GlowOrb color="#00ffb3" size={380} opacity={0.1} className="top-60 right-0" />

      <section className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-24 pb-12">
        <div className="max-w-3xl">
          <Overline>{t.contact.overline}</Overline>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter">
            {t.contact.title}
          </h1>
          <p className="mt-5 text-lg text-zinc-400">{t.contact.subtitle}</p>
        </div>
      </section>

      <section className="relative mx-auto max-w-7xl px-6 lg:px-10 pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="smk-card rounded-2xl p-8 lg:p-10 space-y-5"
            noValidate
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label={t.contact.form.name} name="name" value={form.name} onChange={onChange} required testId="input-name" />
              <Field label={t.contact.form.email} name="email" type="email" value={form.email} onChange={onChange} required testId="input-email" />
              <Field label={t.contact.form.company} name="company" value={form.company} onChange={onChange} testId="input-company" />
              <Field label={t.contact.form.phone} name="phone" value={form.phone} onChange={onChange} testId="input-phone" />
            </div>
            <Field label={t.contact.form.subject} name="subject" value={form.subject} onChange={onChange} required testId="input-subject" />
            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                {t.contact.form.message}
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                required
                rows={6}
                data-testid="input-message"
                className="mt-2 w-full bg-[#050505] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#00d4ff] focus:ring-1 focus:ring-[#00d4ff] transition resize-y"
              />
            </div>

            <button
              type="submit"
              disabled={status.state === "loading"}
              data-testid="submit-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full smk-gradient-bg text-black font-semibold hover:brightness-110 transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status.state === "loading" ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {t.contact.form.sending}
                </>
              ) : (
                <>
                  <Send size={16} />
                  {t.contact.form.submit}
                </>
              )}
            </button>

            {status.state === "success" && (
              <div data-testid="form-success" className="flex items-start gap-3 p-4 rounded-lg border border-[#00ffb3]/30 bg-[#00ffb3]/5">
                <CheckCircle2 size={18} className="text-[#00ffb3] mt-0.5" />
                <div>
                  <div className="font-semibold text-white">{t.contact.form.successTitle}</div>
                  <div className="text-sm text-zinc-300">{status.message}</div>
                </div>
              </div>
            )}
            {status.state === "error" && (
              <div data-testid="form-error" className="flex items-start gap-3 p-4 rounded-lg border border-red-400/30 bg-red-500/5">
                <AlertCircle size={18} className="text-red-400 mt-0.5" />
                <div>
                  <div className="font-semibold text-white">{t.contact.form.errorTitle}</div>
                  <div className="text-sm text-zinc-300">{status.message}</div>
                </div>
              </div>
            )}
          </form>
        </div>

        <aside className="lg:col-span-5 space-y-6">
          <div className="smk-card rounded-2xl p-7">
            <Overline>{t.contact.info.title}</Overline>
            <div className="mt-6 space-y-5">
              <InfoRow icon={Mail} label={t.contact.info.emailLabel} value={t.contact.info.emailValue} href="mailto:contact@smaktech.fr" testId="info-email" />
              <InfoRow icon={Clock} label={t.contact.info.hoursLabel} value={t.contact.info.hoursValue} />
              <InfoRow icon={Globe} label={t.contact.info.scopeLabel} value={t.contact.info.scopeValue} />
            </div>
          </div>

          <div className="smk-card-highlight rounded-2xl p-7">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#00ffb3]">
              SMAKTECH
            </p>
            <h3 className="mt-2 text-xl font-semibold">Cloud Computing • Infrastructure • DevOps</h3>
            <p className="mt-3 text-sm text-zinc-400">
              Conseil, conception, déploiement et exploitation de plateformes cloud — privées, publiques et hybrides.
            </p>
          </div>
        </aside>
      </section>
    </div>
  );
};

const Field = ({ label, name, value, onChange, type = "text", required = false, testId }) => (
  <div>
    <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
      {label}{required && <span className="text-[#00d4ff]"> *</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testId}
      className="mt-2 w-full bg-[#050505] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#00d4ff] focus:ring-1 focus:ring-[#00d4ff] transition"
    />
  </div>
);

const InfoRow = ({ icon: Icon, label, value, href, testId }) => (
  <div className="flex items-start gap-4">
    <div className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
      <Icon size={15} className="text-[#00d4ff]" />
    </div>
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      {href ? (
        <a href={href} data-testid={testId} className="mt-1 block text-sm text-white hover:smk-gradient-text transition">
          {value}
        </a>
      ) : (
        <div className="mt-1 text-sm text-white">{value}</div>
      )}
    </div>
  </div>
);

export default Contact;
